/**
 * Day streak — the child's habit counter (specs/day-streak.md).
 *
 * Phaser-free and clock-free on purpose: every function takes `today` as a local `YYYY-MM-DD`
 * string, so the arithmetic is unit-tested without a canvas or a real clock. Storage is one global
 * key (`abcs.streak.v1`), NOT per lesson — a streak describes the child's habit, not a journey.
 *
 * Cosmetic and motivational only. Nothing here touches mastery, difficulty or the treat banks.
 */

/** Correct smashes that make a calendar day count (spec §2.1). */
export const STREAK_MIN_SMASHES = 5;
/** Rest-day freezes the bank holds (spec §2.4). */
export const STREAK_MAX_FREEZES = 2;
/** Streak length at which a freeze is earned, and every multiple of it. */
export const STREAK_FREEZE_EVERY = 7;
export const STREAK_MILESTONES = [3, 7, 14, 30, 60, 100, 365] as const;
export const STREAK_KEY = 'abcs.streak.v1';

export interface StreakData {
  v: 1;
  current: number;
  best: number;
  /** Local YYYY-MM-DD of the last QUALIFYING day. */
  lastDay: string | null;
  /** Local date `todaySmashes` belongs to. */
  todayDay: string | null;
  todaySmashes: number;
  freezes: number;
  milestonesSeen: number[];
  /** Qualifying local dates of the last 7 days, oldest first. */
  week: string[];
}

export type StreakEventKind = 'ignite' | 'extend' | 'saved' | 'restart';

export interface StreakEvent {
  kind: StreakEventKind;
  current: number;
  /** Set when `current` just reached a milestone not celebrated before. */
  milestone?: number;
  /** True when this day earned a rest-day freeze. */
  freezeEarned?: boolean;
}

export type StreakState = 'lit' | 'pending' | 'rescuable' | 'cold';

export interface StreakView {
  current: number;
  best: number;
  state: StreakState;
  freezes: number;
  /** Seven entries, oldest first, the last one being today. */
  week: boolean[];
  /** Whether a streak was ever started on this device. */
  started: boolean;
  /** The next milestone above `current`, or null past the last one. */
  nextMilestone: number | null;
}

export function emptyStreak(): StreakData {
  return {
    v: 1, current: 0, best: 0, lastDay: null, todayDay: null, todaySmashes: 0,
    freezes: 0, milestonesSeen: [], week: [],
  };
}

// ── dates ────────────────────────────────────────────────────────────────────

/** Device-LOCAL calendar date, not UTC — a child's day is the one on the wall (spec §2.2). */
export function localYmd(d: Date = new Date()): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

function dayNumber(ymd: string): number | null {
  const m = YMD.exec(ymd);
  if (!m) return null;
  // UTC of the *calendar* date: DST makes a local day 23 or 25 hours, a calendar day is always 1.
  return Math.floor(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86_400_000);
}

/** Calendar days from `from` to `to` (negative when `to` is earlier); NaN when either is malformed. */
export function dayGap(from: string, to: string): number {
  const a = dayNumber(from);
  const b = dayNumber(to);
  return a === null || b === null ? NaN : b - a;
}

/** `ymd` shifted by `days` calendar days. */
export function addDays(ymd: string, days: number): string {
  const n = dayNumber(ymd);
  if (n === null) return ymd;
  const d = new Date((n + days) * 86_400_000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

// ── the arithmetic ───────────────────────────────────────────────────────────

function pushWeek(week: string[], today: string): string[] {
  const out = week.filter((d) => {
    const g = dayGap(d, today);
    return g >= 0 && g < 7 && d !== today;
  });
  out.push(today);
  return out;
}

/**
 * One correct smash on `today`. Returns the new data and, only on the smash that makes today
 * qualify, the event to celebrate. Pure: `data` is never mutated.
 */
export function recordSmash(
  data: StreakData, today: string,
): { data: StreakData; event: StreakEvent | null } {
  // A clock set back before the last qualifying day: change nothing (spec §2.3).
  if (data.lastDay !== null) {
    const back = dayGap(data.lastDay, today);
    if (back < 0 || Number.isNaN(back)) return { data, event: null };
  }
  // Already counted today.
  if (data.lastDay === today) return { data, event: null };

  const next: StreakData = { ...data, milestonesSeen: [...data.milestonesSeen], week: [...data.week] };
  if (next.todayDay !== today) { next.todayDay = today; next.todaySmashes = 0; }
  next.todaySmashes += 1;
  if (next.todaySmashes < STREAK_MIN_SMASHES) return { data: next, event: null };

  // Today qualifies.
  let kind: StreakEventKind;
  if (next.lastDay === null) {
    kind = 'ignite';
    next.current = 1;
  } else {
    const gap = dayGap(next.lastDay, today);
    if (gap === 1) {
      kind = 'extend';
      next.current += 1;
    } else if (gap === 2 && next.freezes > 0) {
      kind = 'saved';
      next.freezes -= 1;
      next.current += 1;
    } else {
      kind = 'restart';
      next.current = 1;
    }
  }
  next.lastDay = today;
  next.best = Math.max(next.best, next.current);
  next.week = pushWeek(next.week, today);

  const event: StreakEvent = { kind, current: next.current };
  if (kind === 'extend' || kind === 'saved') {
    if (next.current % STREAK_FREEZE_EVERY === 0 && next.freezes < STREAK_MAX_FREEZES) {
      next.freezes += 1;
      event.freezeEarned = true;
    }
  }
  if ((STREAK_MILESTONES as readonly number[]).includes(next.current)
    && !next.milestonesSeen.includes(next.current)) {
    next.milestonesSeen.push(next.current);
    event.milestone = next.current;
  }
  return { data: next, event };
}

/** What the menu draws — the scene does no date maths of its own (spec §4.2). */
export function viewOf(data: StreakData, today: string): StreakView {
  const started = data.lastDay !== null;
  const gap = data.lastDay === null ? NaN : dayGap(data.lastDay, today);

  let state: StreakState = 'cold';
  if (started && !Number.isNaN(gap)) {
    if (gap === 0) state = 'lit';
    else if (gap < 0 || gap === 1) state = 'pending';   // a clock set back counts as "still alive"
    else if (gap === 2 && data.freezes > 0) state = 'rescuable';
  }

  const week: boolean[] = [];
  for (let i = 6; i >= 0; i--) week.push(data.week.includes(addDays(today, -i)));

  const next = (STREAK_MILESTONES as readonly number[]).find((m) => m > data.current) ?? null;
  return {
    current: data.current, best: data.best, state, freezes: data.freezes, week, started,
    nextMilestone: next,
  };
}

/** Star decoration tier for the visuals: 1 (day 1-2) … 5 (30+). */
export function starTier(current: number): 1 | 2 | 3 | 4 | 5 {
  if (current >= 30) return 5;
  if (current >= 14) return 4;
  if (current >= 7) return 3;
  if (current >= 3) return 2;
  return 1;
}

/** Bucket for the one analytics event — never the raw number (spec §4.4). */
export function streakBucket(current: number): string {
  if (current <= 1) return '1';
  if (current <= 3) return '2-3';
  if (current <= 6) return '4-6';
  if (current <= 13) return '7-13';
  if (current <= 29) return '14-29';
  return '30+';
}

// ── storage ──────────────────────────────────────────────────────────────────

function asInt(v: unknown, lo: number, hi: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, Math.floor(v))) : lo;
}

function asYmd(v: unknown): string | null {
  return typeof v === 'string' && YMD.test(v) ? v : null;
}

/** Defensive parse: every field defaults on its own; an unknown version is treated as empty. */
export function parseStreak(raw: string | null): StreakData {
  if (!raw) return emptyStreak();
  try {
    const p: unknown = JSON.parse(raw);
    if (!p || typeof p !== 'object' || (p as { v?: unknown }).v !== 1) return emptyStreak();
    const o = p as Record<string, unknown>;
    const current = asInt(o.current, 0, 100_000);
    return {
      v: 1,
      current,
      best: Math.max(current, asInt(o.best, 0, 100_000)),
      lastDay: asYmd(o.lastDay),
      todayDay: asYmd(o.todayDay),
      todaySmashes: asInt(o.todaySmashes, 0, STREAK_MIN_SMASHES),
      freezes: asInt(o.freezes, 0, STREAK_MAX_FREEZES),
      milestonesSeen: Array.isArray(o.milestonesSeen)
        ? o.milestonesSeen.filter((n): n is number => typeof n === 'number' && Number.isFinite(n))
        : [],
      week: Array.isArray(o.week)
        ? o.week.filter((d): d is string => typeof d === 'string' && YMD.test(d)).slice(-7)
        : [],
    };
  } catch {
    return emptyStreak();
  }
}

export function loadStreak(): StreakData {
  try {
    return parseStreak(localStorage.getItem(STREAK_KEY));
  } catch {
    return emptyStreak();   // blocked storage (private window): the menu must still render
  }
}

export function saveStreak(data: StreakData): void {
  try {
    localStorage.setItem(STREAK_KEY, JSON.stringify(data));
  } catch {
    /* storage blocked or full — the streak simply does not persist this time */
  }
}
